import {useCallback, useRef, useState} from 'react'
import {StarIcon, StarFillIcon, TriangleDownIcon} from '@primer/octicons-react'
import {Button, ButtonGroup, ConfirmationDialog, CounterLabel, IconButton, Tooltip} from '@primer/react'
import {Count} from '@github-ui/count'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {useMediaQuery} from '@github-ui/use-media-query'
import {useStarMutation} from './hooks/use-star-mutation'
import {StarListMenu} from './components/StarListMenu'
import styles from './StarButton.module.css'

/** Props for the StarButton component */
export interface StarButtonProps {
  /**
   * The size of the button.
   * @default "small"
   */
  size?: 'small' | 'medium'

  /** Whether the repository is currently starred by the user */
  starred: boolean
  /** Total number of stars on the repository */
  starCount: number
  /** URL path to star the repository */
  starPath: string
  /** URL path to unstar the repository */
  unstarPath: string
  /** Whether the current user has permission to star */
  canStar: boolean
  /** Whether the current user is logged in */
  isLoggedIn: boolean
  /** URL to fetch user lists on demand */
  listsApiPath: string
  /** URL to update list membership (PUT) */
  listsUpdatePath: string
  /** Full repository name with owner, e.g. "octocat/hello-world" */
  repoNameWithOwner?: string
  /** Whether the viewer is an EMU blocked from contributing */
  emuContributionBlocked?: boolean
}

/**
 * Star/unstar button with counter for repository headers.
 *
 * Renders responsively, showing only the star toggle icon below Primer's md breakpoint.
 * At larger widths, it renders as a split button:
 * - Left: star/unstar toggle (or login link) with count
 * - Right: dropdown caret opening the star lists menu
 */
export function StarButton({
  size = 'small',
  starred: initialStarred,
  starCount: initialCount,
  starPath,
  unstarPath,
  canStar,
  isLoggedIn,
  listsApiPath,
  listsUpdatePath,
  repoNameWithOwner,
  emuContributionBlocked,
}: StarButtonProps) {
  const [listsOpen, setListsOpen] = useState(false)
  const dropdownButtonRef = useRef<HTMLButtonElement>(null)
  const toggleButtonRef = useRef<HTMLButtonElement>(null)
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  const closeLists = useCallback(() => setListsOpen(false), [])
  useMediaQuery('(max-width: calc(48rem - 0.02px))', matches => {
    if (!matches || !listsOpen) return

    closeLists()
    setTimeout(() => toggleButtonRef.current?.focus(), 0)
  })

  const {
    starred,
    starCount,
    toggleStar,
    pendingUnstarConfirmation,
    confirmUnstar,
    dismissUnstarConfirmation,
    isLoading: starIsLoading,
    syncExternalState,
  } = useStarMutation({
    starPath,
    unstarPath,
    initialStarred,
    initialCount,
    listsApiPath,
    onUnstarSuccess: closeLists,
  })

  const handleToggleStar = useCallback(() => {
    sendClickAnalyticsEvent({
      category: 'REPOSITORY_OVERVIEW',
      action: starred ? 'click:unstar' : 'click:star',
      label: 'location:header;ref_cta:star_button',
    })
    toggleStar()
  }, [sendClickAnalyticsEvent, starred, toggleStar])

  if (!isLoggedIn) {
    const loginReturnPath = `/${repoNameWithOwner ?? ''}`
    const loginHref = `/login?return_to=${encodeURIComponent(loginReturnPath)}`
    const loginLabel = 'You must be signed in to star a repository'
    const sendLoginClick = () =>
      sendClickAnalyticsEvent({
        category: 'REPOSITORY_OVERVIEW',
        action: 'click:star_login',
        label: 'location:header;ref_cta:star_button',
      })

    return (
      <Tooltip text={loginLabel} type="description" direction="s">
        <Button
          as="a"
          href={loginHref}
          size={size}
          leadingVisual={StarIcon}
          data-testid="star-button"
          className={styles.IconOnlyWhenNarrow}
          onClick={sendLoginClick}
        >
          Star
          <CounterLabel className="ml-1">
            <Count value={starCount} />
          </CounterLabel>
        </Button>
      </Tooltip>
    )
  }

  const starTooltip = starred
    ? `Unstar ${repoNameWithOwner ?? 'this repository'}`
    : `Star ${repoNameWithOwner ?? 'this repository'}`
  const disabledTooltip = emuContributionBlocked
    ? 'You cannot star repositories outside of your enterprise'
    : `You can't star ${repoNameWithOwner ?? 'this repository'}`

  const ariaText = canStar ? starTooltip : disabledTooltip

  const isButtonLoading = starIsLoading || Boolean(pendingUnstarConfirmation)

  const starIcon = () => (starred ? <StarFillIcon className={styles.starredIcon} /> : <StarIcon />)
  const toggleButton = (
    <Tooltip text={ariaText} type="description" direction="s">
      <Button
        ref={toggleButtonRef}
        size={size}
        loading={isButtonLoading}
        inactive={!canStar}
        leadingVisual={starIcon}
        onClick={canStar ? handleToggleStar : undefined}
        data-testid="star-button"
        aria-label={ariaText}
        className={styles.IconOnlyWhenNarrow}
      >
        <span>{starred ? 'Starred' : 'Star'}</span>
        <CounterLabel className="ml-1">
          <Count value={starCount} />
        </CounterLabel>
      </Button>
    </Tooltip>
  )

  return (
    <>
      {canStar ? (
        <ButtonGroup className={styles.ResponsiveButtonGroup}>
          {toggleButton}
          <IconButton
            ref={dropdownButtonRef}
            icon={TriangleDownIcon}
            inactive={!canStar}
            size={size}
            aria-label="Star lists"
            aria-haspopup="dialog"
            data-testid="star-dropdown-button"
            className={styles.HiddenWhenNarrow}
            onClick={() => {
              sendClickAnalyticsEvent({
                category: 'REPOSITORY_OVERVIEW',
                action: 'click:star_lists_open',
                label: 'location:header;ref_cta:star_lists_dropdown',
              })
              setListsOpen(prev => !prev)
            }}
          />
        </ButtonGroup>
      ) : (
        toggleButton
      )}
      {canStar && listsOpen && (
        <StarListMenu
          anchorRef={dropdownButtonRef}
          onClose={closeLists}
          listsApiPath={listsApiPath}
          repoStarred={starred}
          starCount={starCount}
          listsUpdatePath={listsUpdatePath}
          onStarStateChange={syncExternalState}
        />
      )}

      {pendingUnstarConfirmation && (
        <ConfirmationDialog
          title="Unstar this repository?"
          confirmButtonType="danger"
          confirmButtonContent="Unstar"
          cancelButtonContent="Cancel"
          onClose={gesture => {
            if (gesture === 'confirm') {
              sendClickAnalyticsEvent({
                category: 'REPOSITORY_OVERVIEW',
                action: 'click:unstar_confirm',
                label: 'location:header;ref_cta:unstar_confirmation_dialog',
              })
              confirmUnstar()
            } else {
              sendClickAnalyticsEvent({
                category: 'REPOSITORY_OVERVIEW',
                action: 'click:unstar_cancel',
                label: 'location:header;ref_cta:unstar_confirmation_dialog',
              })
              dismissUnstarConfirmation()
            }
          }}
        >
          <p>
            This will remove {pendingUnstarConfirmation.repoNameWithOwner} from the{' '}
            {pendingUnstarConfirmation.listsWithCount} that it&apos;s been added to.
          </p>
        </ConfirmationDialog>
      )}
    </>
  )
}
